import { useEffect, useRef } from 'react';

import { useMap } from '@/hooks/useMap';
import { getDesignationColor } from '@/services/public-land-colors';
import { CLUSTER_CIRCLE_LAYER } from '@/components/map/map-utils';
import {
  PUBLIC_LAND_SOURCE_ID,
  PUBLIC_LAND_SOURCE_LAYER,
  PUBLIC_LAND_FILL_LAYER,
  PUBLIC_LAND_LINE_LAYER,
  WARN_MISSING_SOURCE_LAYER,
} from '@/components/map/public-land-constants';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PublicLandFeatureProps {
  designation: string;
  name: string;
  manager: string;
  acres: number | string;
}

interface PublicLandLayerProps {
  visible: boolean;
  onFeatureClick: (props: PublicLandFeatureProps) => void;
}

// ─── Designation color match expression ──────────────────────────────────────
// MapLibre GL match expression over d_Des_Tp (accessed as 'designation' via feature properties).

type ExprTuple = [string, string, ...string[]];

function buildColorExpression(): ExprTuple {
  return [
    'match',
    ['get', 'designation'],
    'National Forest', getDesignationColor('National Forest'),
    'BLM', getDesignationColor('BLM'),
    'State WMA', getDesignationColor('State WMA'),
    'State Forest', getDesignationColor('State Forest'),
    'State/County Park', getDesignationColor('State/County Park'),
    'County Forest', getDesignationColor('County Forest'),
    'Waterfowl Production Area', getDesignationColor('Waterfowl Production Area'),
    getDesignationColor(null), // fallback warm gray
  // CAST: MapLibre match expression array — TS types don't accept heterogeneous tuples directly
  ] as unknown as ExprTuple;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Renders public land polygons from a PMTiles vector source on the MapLibre map.
 * Must be rendered inside a HuntMap (MapProvider) context.
 * Requires VITE_PUBLIC_LAND_PMTILES_URL to be set; logs a warning and is a no-op if missing.
 */
export function PublicLandLayer({ visible, onFeatureClick }: PublicLandLayerProps) {
  const map = useMap();
  const mountedRef = useRef(true);
  const visibleRef = useRef(visible);
  const onFeatureClickRef = useRef(onFeatureClick);

  // Keep callback ref current so click handler always uses latest prop — in useEffect to avoid render-phase side effect
  useEffect(() => {
    onFeatureClickRef.current = onFeatureClick;
  }, [onFeatureClick]);

  const pmtilesUrl = import.meta.env.VITE_PUBLIC_LAND_PMTILES_URL;

  function removeLayers() {
    try {
      if (map.getLayer(PUBLIC_LAND_LINE_LAYER)) map.removeLayer(PUBLIC_LAND_LINE_LAYER);
      if (map.getLayer(PUBLIC_LAND_FILL_LAYER)) map.removeLayer(PUBLIC_LAND_FILL_LAYER);
      if (map.getSource(PUBLIC_LAND_SOURCE_ID)) map.removeSource(PUBLIC_LAND_SOURCE_ID);
    } catch { /* map may be destroyed or style not loaded */ }
  }

  function addLayers(url: string) {
    if (!mountedRef.current) return;
    try {
      removeLayers();

      map.addSource(PUBLIC_LAND_SOURCE_ID, {
        type: 'vector',
        url: `pmtiles://${url}`,
      });

      // Insert public land layers beneath cluster circles when they exist
      const beforeId = map.getLayer(CLUSTER_CIRCLE_LAYER) ? CLUSTER_CIRCLE_LAYER : undefined;

      const colorExpr = buildColorExpression();

      map.addLayer(
        {
          id: PUBLIC_LAND_FILL_LAYER,
          type: 'fill',
          source: PUBLIC_LAND_SOURCE_ID,
          'source-layer': PUBLIC_LAND_SOURCE_LAYER,
          minzoom: 10,
          // CAST: MapLibre paint expression type is broad; the match expression is valid
          paint: { 'fill-color': colorExpr as unknown as string, 'fill-opacity': 0.4 },
          layout: { visibility: visibleRef.current ? 'visible' : 'none' },
        },
        beforeId,
      );

      map.addLayer(
        {
          id: PUBLIC_LAND_LINE_LAYER,
          type: 'line',
          source: PUBLIC_LAND_SOURCE_ID,
          'source-layer': PUBLIC_LAND_SOURCE_LAYER,
          minzoom: 10,
          // CAST: same as fill — match expression is structurally valid
          paint: { 'line-color': colorExpr as unknown as string, 'line-width': 1.5, 'line-opacity': 0.7 },
          layout: { visibility: visibleRef.current ? 'visible' : 'none' },
        },
        beforeId,
      );

      // Preflight check — verify expected source-layer name in vector metadata
      const source = map.getSource(PUBLIC_LAND_SOURCE_ID);
      if (source && 'vectorLayerIds' in source) {
        const ids = (source as { vectorLayerIds?: string[] }).vectorLayerIds;
        if (ids && !ids.includes(PUBLIC_LAND_SOURCE_LAYER)) {
          console.warn(WARN_MISSING_SOURCE_LAYER);
        }
      }
    } catch { /* map may be destroyed */ }
  }

  function applyVisibility(v: boolean) {
    const vStr = v ? 'visible' : 'none';
    try {
      if (map.getLayer(PUBLIC_LAND_FILL_LAYER)) map.setLayoutProperty(PUBLIC_LAND_FILL_LAYER, 'visibility', vStr);
      if (map.getLayer(PUBLIC_LAND_LINE_LAYER)) map.setLayoutProperty(PUBLIC_LAND_LINE_LAYER, 'visibility', vStr);
    } catch { /* map may be destroyed */ }
  }

  // All hooks declared before any conditional logic — RULE-024
  useEffect(() => {
    visibleRef.current = visible;
    applyVisibility(visible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  useEffect(() => {
    if (!pmtilesUrl) {
      // No URL configured — layer is disabled. Warn once per mount.
      console.warn('PublicLandLayer: VITE_PUBLIC_LAND_PMTILES_URL is not set — layer disabled');
      return;
    }

    mountedRef.current = true;

    function handleStyleLoad() {
      // CAST: pmtilesUrl is non-null here — the outer if-guard returns early when it is falsy
      addLayers(pmtilesUrl as string);
    }

    function handleFillClick(e: unknown) {
      // CAST: MapLibre layer click event — features array on the event object
      const evt = e as { features?: Array<{ properties: Record<string, unknown> }> };
      const features = evt.features ?? [];
      if (features.length === 0) return;
      const props = features[0].properties;
      // CAST: PMTiles feature properties are Record<string, unknown>; narrowing each field to its expected type
      onFeatureClickRef.current({
        designation: (props['d_Des_Tp'] as string | undefined) ?? '',
        name: (props['Unit_Nm'] as string | undefined) ?? '',
        manager: (props['d_Mang_Name'] as string | undefined) ?? '',
        acres: (props['GIS_Acres'] as number | string | undefined) ?? '',
      });
    }

    function handleMouseEnter() {
      map.getCanvas().style.cursor = 'pointer';
    }

    function handleMouseLeave() {
      map.getCanvas().style.cursor = '';
    }

    map.on('style.load', handleStyleLoad);
    if (map.isStyleLoaded()) addLayers(pmtilesUrl);

    map.on('click', PUBLIC_LAND_FILL_LAYER, handleFillClick);
    map.on('click', PUBLIC_LAND_LINE_LAYER, handleFillClick);
    map.on('mouseenter', PUBLIC_LAND_FILL_LAYER, handleMouseEnter);
    map.on('mouseleave', PUBLIC_LAND_FILL_LAYER, handleMouseLeave);
    map.on('mouseenter', PUBLIC_LAND_LINE_LAYER, handleMouseEnter);
    map.on('mouseleave', PUBLIC_LAND_LINE_LAYER, handleMouseLeave);

    return () => {
      mountedRef.current = false;
      map.off('style.load', handleStyleLoad);
      map.off('click', PUBLIC_LAND_FILL_LAYER, handleFillClick);
      map.off('click', PUBLIC_LAND_LINE_LAYER, handleFillClick);
      map.off('mouseenter', PUBLIC_LAND_FILL_LAYER, handleMouseEnter);
      map.off('mouseleave', PUBLIC_LAND_FILL_LAYER, handleMouseLeave);
      map.off('mouseenter', PUBLIC_LAND_LINE_LAYER, handleMouseEnter);
      map.off('mouseleave', PUBLIC_LAND_LINE_LAYER, handleMouseLeave);
      removeLayers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pmtilesUrl]);

  return null;
}
